import { useConfig } from "hooks/use-config";
import React, { startTransition, useEffect, useState } from "react";
import { useTheme } from "hooks/use-theme";
import Loading from "components/loading";
import { useQuery } from "@tanstack/react-query";
import { useUserData } from "store/user-data";
import { getStatisticsAction } from "action/stat-action";
import Chart from "react-apexcharts";

function formatDate(inputDate: string): string {
  const parts = inputDate.split("-");
  const date = new Date(inputDate);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const month = months[date.getMonth()];

  if (parts.length === 2) {
    // YYYY-MM
    return `${month} ${parts[0]}`;
  }

  const day = date.getDate();
  return `${month} ${day}`;
}

interface VisitorsReportChartProps {
  height?: number;
  chartType?: "bar" | "area";
  series?: any[];
  chartColors?: string[];
  timeframe?: string;
}

const VisitorsReportChart = ({
  height = 400,
  chartType = "area",
  chartColors = ["#6b7a3f", "#a8a854", "#c97b4a"],
  timeframe = "28d",
}: VisitorsReportChartProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [config] = useConfig();
  const { theme: mode } = useTheme();

  const { userData } = useUserData();
  const businessId = userData.business_id;

  const { data: response, isLoading: queryLoading } = useQuery({
    queryKey: ["statistics", businessId, timeframe],
    queryFn: () => getStatisticsAction(timeframe),
    enabled: !!businessId,
  });

  const statsData = response?.data;
  const [chart_data, setChart_data] = useState([]);
  const [chart_labels, setChart_labels] = useState([]);

  // Loading state to delay rendering until data is fetched
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (statsData?.total_revenue != null) {
      startTransition(() => {
        const chart_data =
          statsData?.visitors_chart?.map((data) => {
            return data.visits;
          }) || [];
        const chart_labels =
          statsData?.visitors_chart?.map((data) => {
            return formatDate(data.date);
          }) || [];
        setChart_data(chart_data);
        setChart_labels(chart_labels);
        setIsLoading(false); // Set loading to false when data is available
      });
    } else if (!queryLoading) {
      setIsLoading(false);
    }
  }, [statsData, queryLoading]);

  const defaultSeries = [
    // {
    //   name: "Net Profit",
    //   data: [44, 55, 57, 56, 61, 58, 63, 60, 66],
    // },
    {
      name: "Visits",
      data: chart_data,
    },
    // {
    //   name: "Free Cash Flow",
    //   data: [35, 41, 36, 26, 45, 48, 52, 53, 41],
    // }
  ];

  const options: any = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        endingShape: "rounded",
        columnWidth: "45%",
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      fontFamily: "'Inter Variable', Inter, sans-serif",
      offsetY: -30,
      markers: {
        width: 8,
        height: 8,
        offsetY: -1,
        offsetX: -5,
        radius: 12,
      },
      labels: {
        colors: mode === "dark" ? "#c9cbbd" : "#6d7468",
      },
      itemMargin: {
        horizontal: 18,
        vertical: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    yaxis: {
      labels: {
        style: {
          colors: mode === "dark" ? "#c9cbbd" : "#6d7468",
          fontFamily: "'Inter Variable', Inter, sans-serif",
        },
      },
    },
    xaxis: {
      categories: chart_labels,
      labels: {
        style: {
          colors: mode === "dark" ? "#c9cbbd" : "#6d7468",
          fontFamily: "'Inter Variable', Inter, sans-serif",
          fontSize: "8px",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      // y: {
      //     formatter: function (val: number) {
      //         return "₦" + val;
      //     },
      // },
    },
    colors: chartColors,
    grid: {
      show: false,
      borderColor: mode === "dark" ? "#2f352b" : "#e5e6e0",
      strokeDashArray: 10,
      position: "back",
    },
    responsive: [
      {
        breakpoint: 600,
        options: {
          legend: {
            position: "bottom",
            offsetY: 8,
            horizontalAlign: "center",
          },
          plotOptions: {
            bar: {
              columnWidth: "80%",
            },
          },
        },
      },
    ],
  };

  // Conditionally render loading or chart based on data availability
  return isLoading ? (
    <Loading />
  ) : chart_data.length === 0 ? (
    <div
      style={{
        textAlign: "center",
        padding: "20px",
        fontSize: "18px",
        color: mode === "dark" ? "#c9cbbd" : "#6d7468",
      }}
    >
      No Data Available
    </div>
  ) : (
    <Chart
      options={options}
      series={defaultSeries}
      type={chartType}
      height={height}
      width={"100%"}
    />
  );
};

export default VisitorsReportChart;
